import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../axios-Instance";
import { queryKeys } from "../react-query/constants";
import { getLoginToken } from "../storage";
import {
  useForgotPassword,
  useLogin,
  useRegister,
  useResetPassword,
  useUsers,
  useUpdateUser,
  useApproveAccount,
  useDeleteAccount,
  useUpdateProfile,
  useGoogleLogin,
  useMicrosoftLogin,
  useCreateAdmin,
  useUpdatePhoto,
  useInviteUser,
  useDeleteInvitedUser,
  useUpdatePwdInvitedUser,
  useUserPayment,
  useApproveKyc,
  useRejectKYC,
  useLogout,
  useUserPremium,
} from "./auth";
import { toastOptions } from "../utils";
import { toast } from "react-toastify";
import {
  useAssessment,
  useCreateAssessment,
  useDeleteAssessment,
  useUpdateAssessment,
} from "./assessment";
import {
  useAssessmentResponse,
  useCreateAssessmentResponse,
  useDeleteAssessmentResponse,
  useFastTrackResponse,
  useMyAssessmentResponse,
  useUpdateAssessmentResponse,
} from "./assessment/responses";
import { useGetOnboardStatus } from "./OnboardResponse";
import {
  useCreateTraining,
  useDeleteTraining,
  useTraining,
  useUpdateTraining,
} from "./training";
import {
  useCreateService,
  useDeleteService,
  useMyService,
  useService,
  useUpdateService,
} from "./service";
import {
  useColor,
  useCreateColor,
  useDeleteColor,
  useUpdateColor,
} from "./color";
import {
  useCreateCategory,
  useDeleteCategory,
  useGetCategory,
  useUpdateCategory,
} from "./category";
import {
  useCreateQuestionnaire,
  useDeleteQuestionnaire,
  useGetQuestionnaire,
  useUpdateQuestionnaire,
} from "./questionnaire";
import {
  useCreateNewsletter,
  useDeleteNewsletter,
  useNewsletter,
  useUpdateNewsletter,
} from "./newsletter";
import {
  useCourses,
  useCreateCourses,
  useDeleteCourses,
  useDeleteVideoSection,
  useUpdateCourses,
  useUpdateVideoSection,
  useUploadVideo,
} from "./courses";
import {
  useBatch,
  useCreateBatch,
  useDeleteBatch,
  useUpdateBatch,
} from "./batch";
import {
  useCreateTeam,
  useDeleteTeam,
  useTeam,
  useTransaction,
  useUpdateTeam,
} from "./batch/team";
import { useCreateKey, useDeleteKey, useKey, useUpdateKey } from "./color/key";
import {
  useCreateIAssessment,
  useDeleteIAssessment,
  useIAssessment,
  useUpdateIAssessment,
} from "./assessment/iassessment";
import {
  useCreateSectionA,
  useDeleteSectionA,
  useSectionA,
  useUpdateSectionA,
} from "./assessment/sectiona";
import {
  useCreateIAssessmentResponse,
  useCreateSectionAResponse,
  useDeleteIAssessmentResponse,
  useIAssessmentResponse,
  useMyIAssessmentResponse,
  useSectionAResponse,
  useUpdateIAssessmentResponse,
} from "./assessment/iresponses";
import {
  useCreateBlog,
  useDeleteBlog,
  useGetBlog,
  useUpdateBlog,
} from "./blog";

const SERVER_ERROR = "There was an error contacting the server.";

export const hooks = {
  useLogin,
  useRegister,
  useResetPassword,
  useForgotPassword,
  useUsers,
  useUpdateUser,
  useApproveAccount,
  useDeleteAccount,
  useUpdateProfile,
  useAssessment,
  useCreateAssessment,
  useUpdateAssessment,
  useDeleteAssessment,
  useAssessmentResponse,
  useCreateAssessmentResponse,
  useUpdateAssessmentResponse,
  useDeleteAssessmentResponse,
  useGetOnboardStatus,
  useTraining,
  useCreateTraining,
  useDeleteTraining,
  useUpdateTraining,
  useGoogleLogin,
  useMicrosoftLogin,
  useCreateAdmin,
  useColor,
  useCreateColor,
  useUpdateColor,
  useDeleteColor,
  useCreateCategory,
  useGetCategory,
  useUpdateCategory,
  useDeleteCategory,
  useGetQuestionnaire,
  useCreateQuestionnaire,
  useDeleteQuestionnaire,
  useUpdateQuestionnaire,
  useNewsletter,
  useCreateNewsletter,
  useUpdateNewsletter,
  useDeleteNewsletter,
  useUpdatePhoto,
  useMyAssessmentResponse,
  useCreateService,
  useDeleteService,
  useService,
  useUpdateService,
  useMyService,
  useFastTrackResponse,
  useDeleteVideoSection,
  useUpdateVideoSection,
  useUploadVideo,
  useCreateCourses,
  useUpdateCourses,
  useCourses,
  useDeleteCourses,
  useCreateBatch,
  useUpdateBatch,
  useBatch,
  useDeleteBatch,
  useCreateTeam,
  useUpdateTeam,
  useTeam,
  useDeleteTeam,
  useInviteUser,
  useDeleteInvitedUser,
  useUpdatePwdInvitedUser,
  useUserPayment,
  useTransaction,
  useCreateKey,
  useUpdateKey,
  useKey,
  useDeleteKey,
  useDeleteIAssessment,
  useIAssessment,
  useCreateIAssessment,
  useUpdateIAssessment,
  useSectionA,
  useCreateSectionA,
  useUpdateSectionA,
  useDeleteSectionA,
  useCreateIAssessmentResponse,
  useUpdateIAssessmentResponse,
  useDeleteIAssessmentResponse,
  useMyIAssessmentResponse,
  useIAssessmentResponse,
  useApproveKyc,
  useRejectKYC,
  useSectionAResponse,
  useCreateSectionAResponse,
  useLogout,
  useUserPremium,
  useGetBlog,
  useCreateBlog,
  useDeleteBlog,
  useUpdateBlog,
};

async function getMe() {
  const data = await axiosInstance({
    url: "/auth/me",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data;
}

export function useGetMe() {
  const fallback = {};
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.user],
    queryFn: () => getMe(),
    onSuccess: () => {},
    onError: (error: any) => {
      const err = error?.response?.data?.error
        ? error?.response?.data?.error
        : SERVER_ERROR;
      toast.error(err, toastOptions);
    },
  });
  return data;
}
